import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import GameOverModal from "../../../components/GameOverModal";
import GameWonModal from "../../../components/GameWonModal";
import { useDarkMode } from "../../../context/DarkModeProvider";
import "../../../assets/css/gamesCss/Tree/bst.css"
import "../../../assets/css/colors.css"
import { useLanguage } from "context/LanguageProvider";


const isValidBST = (arr, index = 0, min = -Infinity, max = Infinity) => {
    if (index >= arr.length || arr[index] === null || arr[index] === undefined) {
        return true;
    }

    const value = arr[index];

    if (value <= min || value >= max) {
        return false;
    }

    const leftChild = 2 * index + 1;
    const rightChild = 2 * index + 2;

    return isValidBST(arr, leftChild, min, value) &&
        isValidBST(arr, rightChild, value, max);
};

const levelOrderToCompactArray = (levelOrder) => {
    if (!levelOrder || levelOrder.length === 0) return [];

    const tree = [];
    tree[0] = levelOrder[0];

    for (let i = 1; i < levelOrder.length; i++) {
        const value = levelOrder[i];
        let currentIndex = 0;

        while (currentIndex < tree.length) {
            if (tree[currentIndex] === null || tree[currentIndex] === undefined) {
                break;
            }

            if (value < tree[currentIndex]) {
                const leftChild = 2 * currentIndex + 1;
                if (tree[leftChild] === null || tree[leftChild] === undefined) {
                    tree[leftChild] = value;
                    break;
                }
                currentIndex = leftChild;
            } else {
                const rightChild = 2 * currentIndex + 2;
                if (tree[rightChild] === null || tree[rightChild] === undefined) {
                    tree[rightChild] = value;
                    break;
                }
                currentIndex = rightChild;
            }
        }
    }

    return tree;
};

// Έλεγχος για INSERT operation
const validateInsertOperation = (originalTree, targetValue, userSequence) => {
    const originalValues = originalTree.filter(v => v !== null && v !== undefined);

    if (userSequence.length !== originalValues.length + 1) {
        return {
            valid: false,
            reason: "Ο αριθμός των κόμβων δεν είναι σωστός"
        };
    }

    const userSet = new Set(userSequence);
    for (const val of originalValues) {
        if (!userSet.has(val)) {
            return {
                valid: false,
                reason: `Λείπει ο κόμβος ${val} από το τελικό δέντρο`
            };
        }
    }

    if (!userSet.has(targetValue)) {
        return {
            valid: false,
            reason: `Ο νέος κόμβος ${targetValue} δεν υπάρχει στη λύση`
        };
    }

    const resultTree = levelOrderToCompactArray(userSequence);

    if (!isValidBST(resultTree)) {
        return {
            valid: false,
            reason: "Το τελικό δέντρο δεν είναι έγκυρο BST (παραβιάζεται η ιδιότητα ordering)"
        };
    }

    return {
        valid: true,
        reason: "Η λύση είναι σωστή!"
    };
};

// Έλεγχος για DELETE operation
const validateDeleteOperation = (originalTree, targetValue, userSequence) => {
    const originalValues = originalTree.filter(v => v !== null && v !== undefined);

    if (userSequence.length !== originalValues.length - 1) {
        return {
            valid: false,
            reason: "Ο αριθμός των κόμβων δεν είναι σωστός"
        };
    }

    if (userSequence.includes(targetValue)) {
        return {
            valid: false,
            reason: `Ο κόμβος ${targetValue} πρέπει να έχει διαγραφεί`
        };
    }

    const userSet = new Set(userSequence);
    for (const val of originalValues) {
        if (val !== targetValue && !userSet.has(val)) {
            return {
                valid: false,
                reason: `Λείπει ο κόμβος ${val} από το τελικό δέντρο`
            };
        }
    }

    const resultTree = levelOrderToCompactArray(userSequence);

    if (!isValidBST(resultTree)) {
        return {
            valid: false,
            reason: "Το τελικό δέντρο δεν είναι έγκυρο BST"
        };
    }

    return {
        valid: true,
        reason: "Η λύση είναι σωστή!"
    };
};

// Έλεγχος για SEARCH operation
const validateSearchOperation = (originalTree, targetValue, userPath) => {
    if (!userPath || userPath.length === 0) {
        return {
            valid: false,
            reason: "Το μονοπάτι αναζήτησης είναι κενό"
        };
    }

    const root = originalTree.find(v => v !== null && v !== undefined);
    if (userPath[0] !== root) {
        return {
            valid: false,
            reason: `Το μονοπάτι πρέπει να ξεκινά από τη ρίζα (${root})`
        };
    }

    const lastValue = userPath[userPath.length - 1];
    if (lastValue !== targetValue) {
        return {
            valid: false,
            reason: `Το μονοπάτι πρέπει να καταλήγει στον κόμβο ${targetValue}`
        };
    }

    for (let i = 0; i < userPath.length - 1; i++) {
        const current = userPath[i];
        const next = userPath[i + 1];

        if (!originalTree.includes(next)) {
            return {
                valid: false,
                reason: `Ο κόμβος ${next} δεν υπάρχει στο δέντρο`
            };
        }
    }

    const uniquePath = new Set(userPath);
    if (uniquePath.size !== userPath.length) {
        return {
            valid: false,
            reason: "Το μονοπάτι περιέχει επαναλαμβανόμενους κόμβους"
        };
    }

    return {
        valid: true,
        reason: "Η λύση είναι σωστή!"
    };
};

// Ενοποιημένη συνάρτηση ελέγχου
const validateUserSolution = (operationType, originalTree, targetValue, userSequence) => {
    switch (operationType) {
        case "insert":
            return validateInsertOperation(originalTree, targetValue, userSequence);
        case "delete":
            return validateDeleteOperation(originalTree, targetValue, userSequence);
        case "search":
            return validateSearchOperation(originalTree, targetValue, userSequence);
        default:
            return {
                valid: false,
                reason: "Άγνωστος τύπος λειτουργίας"
            };
    }
};

// ============= MAIN COMPONENT =============

const BSTOperationsGame = () => {
    const { darkMode } = useDarkMode();
    const [currentStage, setCurrentStage] = useState(1);
    const [tree, setTree] = useState([]);
    const [targetValue, setTargetValue] = useState(null);
    const [userInput, setUserInput] = useState("");
    const [currentStep, setCurrentStep] = useState(0);
    const [message, setMessage] = useState("");
    const [gameState, setGameState] = useState("playing");
    const [lives, setLives] = useState(3);
    const [showHint, setShowHint] = useState(false);
    const [wrongAttempts, setWrongAttempts] = useState(0);
    const [showGameOver, setShowGameOver] = useState(false);
    const [showGameWon, setShowGameWon] = useState(false);
    const [operationType, setOperationType] = useState("insert");
    const [submittedValues, setSubmittedValues] = useState([]);
    const [expectedLength, setExpectedLength] = useState(0);
    const inputRef = useRef(null);
    const { selLanguage, toggleLanguage, isLoading } = useLanguage();

    const stages = {
        1: {
            name: "Εισαγωγή στοιχείου",
            type: "insert",
            initialTree: [50, 30, 70, 20, 40, 60, 80, 10, 25, null, 45, 55, 65, 75, 90, null, null, null, null, null, null, null, null, null, null, null, null, null, null, 72, 77],
            target: 73,
        },
        2: {
            name: "Διαγραφή στοιχείου",
            type: "delete",
            initialTree: [60, 40, 85, 25, 50, 75, 95, 15, 30, 45, 55, 70, 80, 90, 100, null, null, null, 35, null, null, null, null, null, null, null, null, null, null, null, null],
            target: 40,
        },
        3: {
            name: "Αναζήτηση στοιχείου",
            type: "search",
            initialTree: [55, 35, 80, 20, 45, 70, 95, 10, 28, 40, 50, 65, 75, 88, 100, null, null, 25, 30, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, null, 32],
            target: 32,
        }
    };

    useEffect(() => {
        initializeStage(currentStage);
    }, [currentStage]);

    useEffect(() => {
        if (inputRef.current && gameState === "playing") {
            inputRef.current.focus();
        }
    }, [currentStep, gameState]);

    const initializeStage = (stage) => {
        const stageData = stages[stage];
        setTree([...stageData.initialTree]);
        setTargetValue(stageData.target);
        setCurrentStep(0);
        setSubmittedValues([]);
        setUserInput("");
        setGameState("playing");
        setShowHint(false);
        setWrongAttempts(0);
        setOperationType(stageData.type);
        setMessage("");

        // Υπολογισμός αναμενόμενου μήκους λύσης
        const originalCount = stageData.initialTree.filter(v => v !== null && v !== undefined).length;
        let expectedLen = 0;

        if (stageData.type === "insert") {
            expectedLen = originalCount + 1;
        } else if (stageData.type === "delete") {
            expectedLen = originalCount - 1;
        } else if (stageData.type === "search") {
            // Για search, υπολογίζουμε το μήκος του μονοπατιού
            let currentIndex = 0;
            let pathLength = 0;
            while (currentIndex < stageData.initialTree.length &&
                stageData.initialTree[currentIndex] !== null &&
                stageData.initialTree[currentIndex] !== undefined) {
                pathLength++;
                if (stageData.initialTree[currentIndex] === stageData.target) break;
                if (stageData.target < stageData.initialTree[currentIndex]) {
                    currentIndex = 2 * currentIndex + 1;
                } else {
                    currentIndex = 2 * currentIndex + 2;
                }
            }
            expectedLen = pathLength;
        }

        setExpectedLength(expectedLen);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (gameState !== "playing" || !userInput.trim()) return;

        const userValue = parseInt(userInput.trim());

        if (isNaN(userValue)) {
            setMessage(selLanguage ? "Παρακαλώ εισάγετε έναν έγκυρο αριθμό!" : "Please enter a valid number!");
            return;
        }

        // Προσθήκη της νέας τιμής στη λίστα
        const newSubmitted = [...submittedValues, userValue];
        setSubmittedValues(newSubmitted);
        setUserInput("");
        setCurrentStep(currentStep + 1);

        // Έλεγχος αν ο χρήστης έχει ολοκληρώσει τη λύση του
        if (newSubmitted.length === expectedLength) {
            // Πλήρης έλεγχος της λύσης
            const validation = validateUserSolution(operationType, tree, targetValue, newSubmitted);

            if (validation.valid) {
                // Σωστή λύση!
                if (currentStage === 3) {
                    setGameState("completed");
                    setShowGameWon(true);
                    setMessage(selLanguage ? "Συγχαρητήρια! Ολοκληρώσατε όλα τα στάδια!" : "Congratulations! You completed all stages!");
                } else {
                    setMessage(selLanguage ? `Τέλεια! Ολοκληρώσατε το στάδιο! Προχωρώντας στο στάδιο ${currentStage + 1}` : `Great! You completed the stage! Moving to stage ${currentStage + 1}`);
                    setTimeout(() => {
                        setCurrentStage(currentStage + 1);
                    }, 3000);
                }
                setShowHint(false);
                setWrongAttempts(0);
            } else {
                // Λάθος λύση
                const newLives = lives - 1;
                setLives(newLives);
                setWrongAttempts(wrongAttempts + 1);
                setShowHint(true);

                if (newLives === 0) {
                    setGameState("lost");
                    setShowGameOver(true);
                    setMessage(selLanguage
                        ? `Έχασες όλες τις ζωές σου! ${validation.reason}`
                        : `You lost all your lives! ${validation.reason}`);
                } else {
                    setMessage(selLanguage
                        ? `${validation.reason}. Προσπάθησε ξανά, σου απομένουν ${newLives} ζωές.`
                        : `${validation.reason}. Try again, you have ${newLives} lives left.`);
                }
            }
        } else {
            // Ενδιάμεσο βήμα - απλά συνεχίζουμε
            setMessage(selLanguage ? "Συνεχίστε με τον επόμενο κόμβο." : "Continue to the next node.");
            setTimeout(() => setMessage(""), 1500);
        }
    };

    const resetGame = () => {
        setCurrentStage(1);
        setLives(3);
        setGameState("playing");
        setMessage("");
        setShowHint(false);
        setWrongAttempts(0);
        setShowGameOver(false);
        setShowGameWon(false);
    };

    const resetCurrentStage = () => {
        initializeStage(currentStage);
    };

    const getNodePositions = (level, nodeIndex) => {
        const baseSpacing = 800;
        const levelSpacing = baseSpacing / Math.pow(2, level);
        const nodesInLevel = Math.pow(2, level);
        const indexInLevel = nodeIndex - (Math.pow(2, level) - 1);
        const startOffset = -(nodesInLevel - 1) * levelSpacing / 2;

        return {
            x: startOffset + indexInLevel * levelSpacing,
            y: level * 80
        };
    };

    const getNodeLevel = (index) => {
        return Math.floor(Math.log2(index + 1));
    };

    const renderEdges = () => {
        const edges = [];
        for (let i = 0; i < tree.length; i++) {
            if (tree[i] === null || tree[i] === undefined) continue;

            const leftChildIndex = 2 * i + 1;
            const rightChildIndex = 2 * i + 2;
            const parentLevel = getNodeLevel(i);
            const parentPos = getNodePositions(parentLevel, i);

            if (leftChildIndex < tree.length && tree[leftChildIndex] !== null && tree[leftChildIndex] !== undefined) {
                const childLevel = getNodeLevel(leftChildIndex);
                const childPos = getNodePositions(childLevel, leftChildIndex);
                edges.push(
                    <line
                        key={`edge-${i}-${leftChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="2"
                        opacity="0.6"
                    />
                );
            }

            if (rightChildIndex < tree.length && tree[rightChildIndex] !== null && tree[rightChildIndex] !== undefined) {
                const childLevel = getNodeLevel(rightChildIndex);
                const childPos = getNodePositions(childLevel, rightChildIndex);
                edges.push(
                    <line
                        key={`edge-${i}-${rightChildIndex}`}
                        x1={parentPos.x}
                        y1={parentPos.y + 25}
                        x2={childPos.x}
                        y2={childPos.y + 25}
                        stroke="#64748b"
                        strokeWidth="2"
                        opacity="0.6"
                    />
                );
            }
        }
        return edges;
    };

    const renderNode = (nodeValue, index) => {
        if (nodeValue === null || nodeValue === undefined) return null;

        const isSubmitted = submittedValues.includes(nodeValue);
        const isTarget = nodeValue === targetValue;
        const level = getNodeLevel(index);
        const position = getNodePositions(level, index);

        return (
            <g key={index}>
                <motion.circle
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    cx={position.x}
                    cy={position.y + 25}
                    r="22"
                    fill={isTarget ? "#ef4444" : isSubmitted ? "#10b981" : "#64748b"}
                    stroke={isTarget ? "#dc2626" : "rgba(255,255,255,0.1)"}
                    strokeWidth={isTarget ? "3" : "2"}
                    style={{
                        filter: isTarget
                            ? "drop-shadow(0 6px 15px rgba(239, 68, 68, 0.5))"
                            : isSubmitted
                                ? "drop-shadow(0 6px 15px rgba(16, 185, 129, 0.4))"
                                : "drop-shadow(0 4px 8px rgba(0,0,0,0.15))",
                        transition: "all 0.3s ease"
                    }}
                />
                <text
                    x={position.x}
                    y={position.y + 30}
                    textAnchor="middle"
                    fill="white"
                    fontSize="13"
                    fontWeight="bold"
                    style={{ pointerEvents: "none" }}
                >
                    {nodeValue}
                </text>
            </g>
        );
    };

    const renderTree = () => {
        const maxLevel = Math.floor(Math.log2(tree.length));
        const svgWidth = 1000;
        const svgHeight = (maxLevel + 2) * 70;

        return (
            <div style={{
                display: "flex",
                justifyContent: "center",
                width: "100%",
                overflowX: "auto",
                marginTop: "20px"
            }}>
                <svg
                    width={svgWidth}
                    height={svgHeight}
                    viewBox={`${-svgWidth / 2} 0 ${svgWidth} ${svgHeight}`}
                    style={{ minWidth: "800px" }}
                >
                    {renderEdges()}
                    {tree.map((nodeValue, index) =>
                        nodeValue !== null && nodeValue !== undefined ? renderNode(nodeValue, index) : null
                    )}
                </svg>
            </div>
        );
    };

    const getOperationDescription = () => {
        switch (operationType) {
            case "insert":
                return selLanguage
                    ? `Έστω ότι εισάγουμε τον κόμβο ${targetValue} στο δέντρο. Γράψτε τους κόμβους με τη σειρά που θα εμφανιστούν στο τελικό δέντρο μετά την προσθήκη. (level-order)`
                    : `Suppose we insert node ${targetValue} into the tree. Write the nodes in the order they will appear in the final tree after the insertion (level-order).`;

            case "delete":
                return selLanguage
                    ? `Διαγράψτε τον κόμβο ${targetValue} από το δέντρο. Γράψτε τους κόμβους με τη σειρά που θα εμφανιστούν στο τελικό δέντρο (level-order).`
                    : `Delete node ${targetValue} from the tree. Write the nodes in the order they will appear in the final tree (level-order).`;

            case "search":
                return selLanguage
                    ? `Αναζητήστε τον κόμβο ${targetValue}. Γράψτε τους κόμβους με τη σειρά που θα τους επισκεφτείτε.`
                    : `Search for node ${targetValue}. Write the nodes in the order you will visit them.`;

            default:
                return "";
        }
    };

    return (
        <div
            className="main-content"
            style={{
                minHeight: "100vh",
                background: darkMode
                    ? "linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)"
                    : "var(--lightModeGameBackground)",
                padding: "20px 10px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center"
            }}>

            <GameOverModal
                isVisible={showGameOver}
                onPlayAgain={resetGame}
            />

            <GameWonModal
                isVisible={showGameWon}
                onPlayAgain={resetGame}
            />

            <div style={{ width: "100%", maxWidth: "1200px" }}>
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                    flexWrap: "wrap",
                    gap: "15px"
                }}>
                    <div style={{
                        color: darkMode ? "white" : "#1e293b",
                        padding: "12px 24px",
                        borderRadius: "12px",
                        fontWeight: "700",
                        fontSize: "18px",
                        border: darkMode ? "2px solid rgba(255, 255, 255, 0.2)" : "2px solid #1e293b",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px"
                    }}>
                        {selLanguage ? `Επίπεδο ${currentStage}/3` : `Stage ${currentStage}/3`}
                    </div>

                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <div style={{ display: "flex", gap: "8px" }}>
                            {[1, 2, 3].map((heartNum) => (
                                <div
                                    key={heartNum}
                                    style={{
                                        fontSize: "28px",
                                        filter: lives >= heartNum
                                            ? "drop-shadow(0 4px 8px rgba(220, 38, 38, 0.5))"
                                            : "grayscale(100%) opacity(0.3)"
                                    }}
                                >
                                    ❤️
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="bstOperationsPron">
                    {getOperationDescription()}
                </div>

                {renderTree()}

                <div style={{
                    background: darkMode ? "rgba(30, 41, 59, 0.8)" : "#fff",
                    borderRadius: "16px",
                    padding: "20px",
                }}>
                    <div
                        style={{
                            color: darkMode ? "#94a3b8" : "#080808ff",
                            marginBottom: "15px",
                            fontSize: "15px"
                        }}>
                        {selLanguage ? `Πρόοδος: ${currentStep} / ${expectedLength}` : `Progress: ${currentStep} / ${expectedLength}`}
                    </div>

                    <form onSubmit={handleSubmit} style={{ display: "flex", gap: "10px", alignItems: "center" }}>
                        <input
                            ref={inputRef}
                            type="text"
                            value={userInput}
                            onChange={(e) => setUserInput(e.target.value)}
                            disabled={gameState !== "playing"}
                            placeholder={selLanguage ? `Εισάγετε κόμβο ${currentStep + 1}...` : `Enter node ${currentStep + 1}...`}
                            style={{
                                flex: 1,
                                padding: "14px 20px",
                                fontSize: "16px",
                                borderRadius: "12px",
                                border: "2px solid #475569",
                                background: darkMode ? "#1e293b" : "var(--lightModeXpContainer)",
                                color: "white",
                                outline: "none",
                                transition: "all 0.3s"
                            }}
                            onFocus={(e) => e.target.style.borderColor = "#3b82f6"}
                            onBlur={(e) => e.target.style.borderColor = "#475569"}
                        />
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            type="submit"
                            disabled={gameState !== "playing" || !userInput.trim()}
                            style={{
                                background: gameState === "playing" && userInput.trim()
                                    ? "linear-gradient(135deg, #10b981 0%, #059669 100%)"
                                    : "linear-gradient(135deg, #6b7280 0%, #4b5563 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "12px",
                                padding: "14px 28px",
                                fontSize: "16px",
                                fontWeight: 600,
                                cursor: gameState === "playing" && userInput.trim() ? "pointer" : "not-allowed",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.2)"
                            }}
                        >
                            {selLanguage ? "Υποβολή" : "Submit"}
                        </motion.button>
                    </form>
                </div>

                <AnimatePresence>
                    {message && (
                        <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            style={{
                                textAlign: "center",
                                padding: "20px",
                                borderRadius: "16px",
                                marginTop: "20px",
                                background: message.includes("Σωστά") || message.includes("Correct") || message.includes("Τέλεια") || message.includes("Great") || message.includes("Συγχαρητήρια") || message.includes("Congratulations")
                                    ? "linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)"
                                    : message.includes("Λάθος") || message.includes("Wrong") || message.includes("Έχασες") || message.includes("lost") || message.includes("Απομένουν") || message.includes("lives left") || message.includes("απομένουν")
                                        ? "linear-gradient(135deg, #fef2f2 0%, #fecaca 100%)"
                                        : "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                                border: `2px solid ${message.includes("Σωστά") || message.includes("Correct") || message.includes("Τέλεια") || message.includes("Great") || message.includes("Συγχαρητήρια") || message.includes("Congratulations") ? "#10b981" :
                                    message.includes("Λάθος") || message.includes("Wrong") || message.includes("Έχασες") || message.includes("lost") || message.includes("Απομένουν") || message.includes("lives left") || message.includes("απομένουν") ? "#ef4444" : "#3b82f6"
                                    }`,
                                color: message.includes("Σωστά") || message.includes("Correct") || message.includes("Τέλεια") || message.includes("Great") || message.includes("Συγχαρητήρια") || message.includes("Congratulations") ? "#059669" :
                                    message.includes("Λάθος") || message.includes("Wrong") || message.includes("Έχασες") || message.includes("lost") || message.includes("Απομένουν") || message.includes("lives left") || message.includes("απομένουν") ? "#dc2626" : "#1d4ed8",
                                fontWeight: 600,
                                fontSize: "1rem",
                                maxWidth: "700px",
                                margin: "0 auto",
                                width: "90%"
                            }}
                        >
                            {message}
                        </motion.div>
                    )}
                </AnimatePresence>

                <div style={{
                    display: "flex",
                    justifyContent: "center",
                    gap: "15px",
                    flexWrap: "wrap",
                    marginTop: "20px"
                }}>
                    <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={resetCurrentStage}
                        style={{
                            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                            color: "white",
                            border: "none",
                            borderRadius: "16px",
                            padding: "14px 28px",
                            fontSize: "16px",
                            fontWeight: 600,
                            cursor: "pointer",
                            boxShadow: "0 6px 16px rgba(239, 68, 68, 0.3)"
                        }}
                    >
                        {selLanguage ? "Επαναφορά Σταδίου" : "Reset Stage"}
                    </motion.button>

                    {(gameState === "lost" || gameState === "completed") && (
                        <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={resetGame}
                            style={{
                                background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                                color: "white",
                                border: "none",
                                borderRadius: "16px",
                                padding: "14px 28px",
                                fontSize: "16px",
                                fontWeight: 600,
                                cursor: "pointer",
                                boxShadow: "0 6px 16px rgba(16, 185, 129, 0.3)"
                            }}
                        >
                            {selLanguage ? "Νέο Παιχνίδι" : "New Game"}
                        </motion.button>
                    )}
                </div>

                <div style={{
                    marginTop: "30px",
                    padding: "20px",
                    background: darkMode ? "rgba(30, 41, 59, 0.5)" : "#fff",
                    borderRadius: "16px",
                    color: "#94a3b8",
                    fontSize: "14px",
                    lineHeight: "1.8"
                }}>

                    <ul style={{ margin: 0, paddingLeft: "20px" }}>
                        {selLanguage ? (
                            <>

                                <li>
                                    Η λύση σας ελέγχεται με βάση τις ιδιότητες του BST, όχι με συγκεκριμένο μονοπάτι.
                                </li>
                            </>
                        ) : (
                            <>
                                <li>
                                    Your solution is validated based on BST properties, not a specific path.
                                </li>
                            </>
                        )}
                    </ul>
                </div>
            </div>
        </div>
    );
};

export default BSTOperationsGame;